import { Actions, Subjects, SubjectTypes } from './ability';
import { MongoAbility } from '@casl/ability';
import { subject as identifySubject, CanParameters } from '@casl/ability';
import { adaptForCasl } from './ability';

type AuthorizationParams<
  A extends Actions,
  S extends Subjects,
  T extends [A, S],
> = CanParameters<T>;

/**
 * Safe wrapper around identifySubject that handles immutable objects
 * Creates a new object if the original is frozen to prevent modification errors
 */
function safeIdentifySubject(subjectType: string, subject: any): any {
  try {
    // Try to use identifySubject directly first
    return identifySubject(subjectType, subject);
  } catch (error) {
    // If it fails due to object being non-extensible, create a new object
    if (error instanceof TypeError && error.message.includes('extensible')) {
      const newSubject = { ...subject };
      return identifySubject(subjectType, newSubject);
    }
    // Re-throw other errors
    throw error;
  }
}

export type AuthorizationChecker<
  A extends Actions = Actions,
  S extends Subjects = Subjects,
  T extends [A, S] = [A, S],
> = (
  action: AuthorizationParams<A, S, T>[0],
  subject: SubjectTypes | { __typename?: string } | undefined | null,
  field?: AuthorizationParams<A, S, T>[2],
) => boolean;

export interface IAuthorizedRoute {
  authAction?: Actions | Actions[];
  authType?: SubjectTypes;
  // TODO: Peter: I think this (the state property) was left from an experiment. We should remove if not used.
  state?: Record<string, any>;
}

export type RouteAuthorizationChecker = (route: IAuthorizedRoute) => boolean;

/**
 * Custom hook that provides canUser functionality with automatic user context
 * This allows developers to use: canUser('update', unit) instead of canUser(user, 'update', unit)
 */
export function useCanUser() {
  // Import here to avoid circular dependencies
  const { useAuthorizer } = require('../AuthorizationContext');
  const { ability } = useAuthorizer();

  return (
    action: Actions,
    subject: SubjectTypes | { __typename?: string } | undefined | null,
    field?: string,
  ): boolean => {
    if (!ability) {
      return false;
    }

    let mySubject = subject;
    if (subject !== undefined && subject !== null) {
      if (typeof subject === 'string') {
        mySubject = subject;
      } else if (subject.__typename) {
        // Apply adaptForCasl automatically for GraphQL type objects
        mySubject = safeIdentifySubject(
          subject.__typename,
          adaptForCasl(subject as any),
        );
      } else {
        mySubject = subject;
      }
    }

    return ability.can(action, mySubject as any, field);
  };
}

/**
 * Legacy canUser function that accepts a user parameter for backward compatibility
 * @deprecated Use useCanUser() hook instead
 */
export function canUser(
  user: any,
  action: Actions,
  subject: SubjectTypes | { __typename?: string } | undefined | null,
  field?: string,
): boolean {
  if (!user?.ability) {
    return false;
  }

  let mySubject = subject;
  if (subject !== undefined && subject !== null) {
    if (typeof subject === 'string') {
      mySubject = subject;
    } else if (subject.__typename) {
      // Apply adaptForCasl automatically for GraphQL type objects
      mySubject = safeIdentifySubject(
        subject.__typename,
        adaptForCasl(subject as any),
      );
    } else {
      mySubject = subject;
    }
  }

  return user.ability.can(action, mySubject as any, field);
}

export function isAuthorized<A extends Actions, S extends Subjects>(
  ability: MongoAbility<[Actions, Subjects]>,
  action: AuthorizationParams<Actions, Subjects, [A, S]>[0],
  subject: SubjectTypes | { __typename?: string } | undefined | null,
  field: AuthorizationParams<Actions, Subjects, [A, S]>[2] | undefined,
) {
  let mySubject = subject;
  if (subject !== undefined && subject !== null) {
    if (typeof subject === 'string') {
      mySubject = subject;
    } else if (subject.__typename) {
      // Apply adaptForCasl automatically for GraphQL type objects
      mySubject = safeIdentifySubject(
        subject.__typename,
        adaptForCasl(subject as any),
      );
    } else {
      mySubject = subject;
    }
  }
  return ability.can(action, mySubject as any, field);
}
